import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Loader2, Plus, Pencil, Trash2, Receipt, AlertTriangle, CheckCircle, FileText, CreditCard, Clock, ArrowRight, RotateCcw, History } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import { useTranslation } from "../contexts/I18nContext";

export default function AutomezziBolloPage() {
  const { t } = useTranslation();
  const { canEdit } = usePermissions();
  const canEditAutomezzi = canEdit("automezzi");
  
  const [bolli, setBolli] = useState([]);
  const [automezzi, setAutomezzi] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [movimenti, setMovimenti] = useState([]);
  const [conti, setConti] = useState([]);
  const [allocazioniInAttesa, setAllocazioniInAttesa] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [linkDialog, setLinkDialog] = useState({ open: false, item: null, type: null });
  
  // Dialog per collegare allocazione a bolli
  const [collegaDialog, setCollegaDialog] = useState({ open: false, allocazione: null });
  const [selectedBolli, setSelectedBolli] = useState([]);
  
  // Loading states
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [linking, setLinking] = useState(false);
  const [collegando, setCollegando] = useState(false);
  
  const [form, setForm] = useState({
    automezzo_id: "",
    data_pagamento: "",
    data_scadenza: "",
    importo: "",
    superbollo: "",
    conto_id: "",
    data_inizio_competenza: "",
    data_fine_competenza: "",
    note: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [bolliRes, autoRes, fattRes, movRes, contiRes, allocRes] = await Promise.all([
        api.get(`${getApiPrefix()}/automezzi/bollo/lista`),
        api.get(`${getApiPrefix()}/automezzi`),
        api.get(`${getApiPrefix()}/automezzi/contabilita/fatture`).catch(() => ({ data: [] })),
        api.get(`${getApiPrefix()}/automezzi/contabilita/movimenti-bancari`).catch(() => ({ data: [] })),
        api.get(`${getApiPrefix()}/accounts`).catch(() => ({ data: [] })),
        api.get(`${getApiPrefix()}/allocazioni-bolli`).catch(() => ({ data: [] }))
      ]);
      setBolli(bolliRes.data);
      setAutomezzi(autoRes.data);
      setFatture(fattRes.data || []);
      setMovimenti(movRes.data || []);
      setConti(contiRes.data || []);
      setAllocazioniInAttesa(allocRes.data || []);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAddDialog = () => {
    setEditingId(null);
    setForm({
      automezzo_id: "",
      data_pagamento: new Date().toISOString().split('T')[0],
      data_scadenza: "",
      importo: "",
      superbollo: "",
      conto_id: "",
      data_inizio_competenza: "",
      data_fine_competenza: "",
      note: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setForm({
      automezzo_id: item.automezzo_id || "",
      data_pagamento: item.data_pagamento || "",
      data_scadenza: item.data_scadenza || "",
      importo: item.importo ? String(item.importo) : "",
      superbollo: item.superbollo ? String(item.superbollo) : "",
      conto_id: item.conto_id || "",
      data_inizio_competenza: item.data_inizio_competenza || "",
      data_fine_competenza: item.data_fine_competenza || "",
      note: item.note || ""
    });
    setDialogOpen(true);
  };

  // Apre il dialog di rinnovo del bollo: precompila con i dati precedenti
  // e sposta la scadenza precedente al pagamento, ricavando la nuova scadenza +1 anno.
  const openRenewDialog = (item) => {
    setEditingId(null);
    const prevScadenza = item.data_scadenza || "";
    let nuovoPagamento = new Date().toISOString().split('T')[0];
    let nuovaScadenza = "";
    if (prevScadenza) {
      try {
        const d = new Date(prevScadenza);
        const newEnd = new Date(d);
        newEnd.setFullYear(newEnd.getFullYear() + 1);
        nuovaScadenza = newEnd.toISOString().split('T')[0];
      } catch (_) { /* fallback empty */ }
    }
    setForm({
      automezzo_id: item.automezzo_id || "",
      data_pagamento: nuovoPagamento,
      data_scadenza: nuovaScadenza,
      importo: item.importo ? String(item.importo) : "",
      superbollo: item.superbollo ? String(item.superbollo) : "",
      conto_id: item.conto_id || "",
      data_inizio_competenza: prevScadenza || "",
      data_fine_competenza: nuovaScadenza,
      note: ""
    });
    setDialogOpen(true);
  };

  // Storico bolli del veicolo selezionato (escluso quello in modifica)
  const storicoVeicolo = form.automezzo_id
    ? bolli
        .filter(b => b.automezzo_id === form.automezzo_id && b.id !== editingId)
        .sort((a, b) => (b.data_scadenza || '').localeCompare(a.data_scadenza || ''))
    : [];

  // Tabella principale: solo il bollo più recente per ogni veicolo (data_scadenza desc, poi data_pagamento).
  // I bolli storici restano accessibili nel pannello "Storico" all'interno del dialog di modifica/rinnovo.
  const bolliDisplay = useMemo(() => {
    const groups = new Map();
    for (const b of bolli) {
      const key = b.automezzo_id || `__no_veicolo_${b.id}`;
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, b);
        continue;
      }
      const cur = `${existing.data_scadenza || ''}_${existing.data_pagamento || ''}`;
      const nxt = `${b.data_scadenza || ''}_${b.data_pagamento || ''}`;
      if (nxt > cur) groups.set(key, b);
    }
    const counts = new Map();
    for (const b of bolli) {
      const k = b.automezzo_id || '';
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    return Array.from(groups.values()).map(b => ({
      ...b,
      _storico_count: counts.get(b.automezzo_id || '') || 1,
    }));
  }, [bolli]);

  const handleSubmit = async () => {
    if (!form.automezzo_id || !form.data_pagamento || !form.data_scadenza || !form.importo) {
      handleApiError({ message: t("errors.requiredFields") });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        data_pagamento: form.data_pagamento,
        data_scadenza: form.data_scadenza,
        importo: parseFloat(form.importo),
        superbollo: form.superbollo ? parseFloat(form.superbollo) : null,
        conto_id: form.conto_id || null,
        data_inizio_competenza: form.data_inizio_competenza || null,
        data_fine_competenza: form.data_fine_competenza || null,
        note: form.note || null
      };
      
      if (editingId) {
        await api.put(`${getApiPrefix()}/automezzi/bollo/${editingId}`, payload);
        showSuccess(t("success.taxUpdated"));
      } else {
        await api.post(`${getApiPrefix()}/automezzi/bollo`, {
          ...payload,
          automezzo_id: form.automezzo_id
        });
        showSuccess(t("success.taxRegistered"));
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`${getApiPrefix()}/automezzi/bollo/${deleteDialog.item.id}`);
      showSuccess(t("success.taxDeleted"));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    for (const id of ids) {
      try {
        await api.delete(`${getApiPrefix()}/automezzi/bollo/${id}`);
        deleted++;
      } catch (err) {
        
      }
    }
    showSuccess(`${deleted} bolli eliminati`);
    fetchData();
  };

  const handleLinkFattura = async (fatturaId) => {
    try {
      await api.post(`${getApiPrefix()}/automezzi/bollo/${linkDialog.item.id}/collega-fattura?fattura_id=${fatturaId || ''}`);
      showSuccess(fatturaId ? "Fattura collegata" : "Collegamento rimosso");
      setLinkDialog({ open: false, item: null, type: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorLink"));
    }
  };

  const handleLinkMovimento = async (movimentoId) => {
    try {
      await api.post(`${getApiPrefix()}/automezzi/bollo/${linkDialog.item.id}/collega-movimento?movimento_bancario_id=${movimentoId || ''}`);
      showSuccess(movimentoId ? "Movimento bancario collegato" : "Collegamento rimosso");
      setLinkDialog({ open: false, item: null, type: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorLink"));
    }
  };

  // Collega allocazione ai bolli selezionati
  const handleCollegaAllocazione = async () => {
    if (selectedBolli.length === 0) {
      handleApiError({ message: t("errors.selectAtLeastOneTax") });
      return;
    }
    try {
      await api.post(`${getApiPrefix()}/allocazioni-bolli/${collegaDialog.allocazione.id}/collega`, {
        bollo_ids: selectedBolli,
        source_type: collegaDialog.allocazione.source_type
      });
      showSuccess(`Allocazione collegata a ${selectedBolli.length} bollo/i`);
      setCollegaDialog({ open: false, allocazione: null });
      setSelectedBolli([]);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorLink"));
    }
  };

  const toggleBollo = (id) => {
    setSelectedBolli(prev => 
      prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]
    );
  };

  const formatCurrency = (val) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val || 0);

  const getAlertBadge = (alert) => {
    if (alert === 'scaduta') return <Badge variant="destructive">{t('common.expired')}</Badge>;
    if (alert === 'in_scadenza') return <Badge className="bg-orange-100 text-orange-700">{t('common.expiringSoon')}</Badge>;
    return <Badge className="bg-green-100 text-green-700">Valido</Badge>;
  };

  const scaduti = bolli.filter(b => b.alert === 'scaduta').length;
  const inScadenza = bolli.filter(b => b.alert === 'in_scadenza').length;
  const validi = bolli.filter(b => !b.alert).length;
  const totaleSuperbollo = bolli.reduce((sum, b) => sum + (b.superbollo || 0), 0);

  const columns = [
    {
      key: 'veicolo',
      label: 'Veicolo',
      getValue: (row) => `${row.marca} ${row.modello}`,
      render: (value, row) => (
        <div>
          <div className="flex items-center gap-1.5">
            <span className="font-medium">{value}</span>
            {row._storico_count > 1 && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 gap-1" title={`${row._storico_count} bolli totali per questo veicolo (storico nel dialog)`}>
                <History className="h-3 w-3" /> {row._storico_count}
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground font-mono">{row.targa}</div>
        </div>
      )
    },
    { key: 'data_pagamento', label: 'Data Pagamento' },
    { key: 'data_scadenza', label: 'Scadenza' },
    { key: 'importo', label: t('common.amount'), align: 'right', format: 'currency' },
    { 
      key: 'superbollo', 
      label: 'Superbollo', 
      align: 'right',
      render: (v) => v ? `€ ${v.toFixed(2)}` : '-'
    },
    {
      key: 'alert',
      label: t('common.status'),
      render: (value) => getAlertBadge(value)
    },
    {
      key: 'contabilita',
      label: 'Contabilità',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => (
        <div className="flex gap-1">
          {row.fattura_id ? (
            <Badge variant="outline" className="text-xs cursor-pointer text-green-600 border-green-300" onClick={() => setLinkDialog({ open: true, item: row, type: 'fattura' })}>
              <FileText className="h-3 w-3 mr-1" />
              F.{row.fattura_numero}
            </Badge>
          ) : (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground" onClick={() => setLinkDialog({ open: true, item: row, type: 'fattura' })}>
              <FileText className="h-3 w-3 mr-1" />
              +Fattura
            </Button>
          )}
          {row.movimento_bancario_id ? (
            <Badge variant="outline" className="text-xs cursor-pointer text-blue-600 border-blue-300" onClick={() => setLinkDialog({ open: true, item: row, type: 'movimento' })}>
              <CreditCard className="h-3 w-3 mr-1" />
              Mov.
            </Badge>
          ) : (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground" onClick={() => setLinkDialog({ open: true, item: row, type: 'movimento' })}>
              <CreditCard className="h-3 w-3 mr-1" />
              +Mov.
            </Button>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openRenewDialog(row)}
            title="Rinnova bollo"
            data-testid={`renew-${row.id}`}
          >
            <RotateCcw className="h-4 w-4 text-blue-600" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditDialog(row)}
            data-testid={`edit-${row.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteDialog({ open: true, item: row })}
            className="text-destructive"
            data-testid={`delete-${row.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="bollo-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("nav.roadTax")}</h1>
        <p className="text-muted-foreground text-sm">Gestisci i pagamenti del bollo auto</p>
      </div>

      {/* Allocazioni in attesa */}
      {allocazioniInAttesa.length > 0 && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <Clock className="h-5 w-5" />
              Allocazioni in Attesa di Collegamento ({allocazioniInAttesa.length})
            </CardTitle>
            <CardDescription>
              Movimenti bancari/carta allocati come &quot;Bollo Auto&quot; da collegare ai bolli registrati
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {allocazioniInAttesa.map(alloc => (
                <div key={alloc.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {alloc.source_type === 'bank' ? 'Banca' : 'Carta'}
                      </Badge>
                      <span className="font-medium">
                        {formatCurrency(alloc.importo)}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {alloc.movimento_data} - {alloc.movimento_descrizione || alloc.descrizione}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setCollegaDialog({ open: true, allocazione: alloc });
                      setSelectedBolli([]);
                    }}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />{t('common.link')}</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-red-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Scaduti</p>
                <p className="text-2xl font-bold text-red-600">{scaduti}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Receipt className="h-8 w-8 text-orange-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">{t('common.expiringSoon')}</p>
                <p className="text-2xl font-bold text-orange-600">{inScadenza}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Validi</p>
                <p className="text-2xl font-bold text-green-600">{validi}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div>
              <p className="text-xs text-muted-foreground uppercase">Superbollo Totale</p>
              <p className="text-2xl font-bold font-mono">€ {totaleSuperbollo.toFixed(2)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Elenco Bolli</CardTitle>
          <CardDescription>
            Una riga per veicolo: viene mostrato il bollo più recente. Quando il bollo viene rinnovato i precedenti restano accessibili nello storico interno al dialog di modifica/rinnovo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={bolliDisplay}
            onDelete={handleBulkDelete}
            idField="id"
            emptyMessage="Nessun bollo registrato"
            exportFileName="bollo_auto"
            toolbar={
              <Button onClick={openAddDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Registra Bollo
              </Button>
            }
          />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? t("roadTax.editTax") : t("roadTax.newTax")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('leasing.vehicle')} *</Label>
              <SearchableSelect
                options={automezzi.map(a => ({ value: a.id, label: `${a.marca} ${a.modello} - ${a.targa}` }))}
                value={form.automezzo_id}
                onValueChange={(v) => setForm({ ...form, automezzo_id: v })}
                disabled={!!editingId}
                placeholder={t("dialogs.selectVehicle")}
                searchPlaceholder="Cerca veicolo..."
                emptyMessage="Nessun veicolo trovato"
              />
            </div>

            {/* Storico bolli del veicolo selezionato */}
            {storicoVeicolo.length > 0 && (
              <div className="border border-purple-200 bg-purple-50/50 rounded-lg p-3" data-testid="storico-bolli">
                <div className="flex items-center gap-2 mb-2">
                  <History className="h-4 w-4 text-purple-700" />
                  <p className="text-sm font-medium text-purple-800">
                    Storico bolli precedenti ({storicoVeicolo.length})
                  </p>
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {storicoVeicolo.map(s => (
                    <div key={s.id} className="flex items-center justify-between text-xs bg-white border border-purple-100 rounded px-2 py-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-muted-foreground">
                          Pagamento: {s.data_pagamento} • Scadenza: {s.data_scadenza}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono">{formatCurrency(s.importo)}{s.superbollo > 0 ? ` + ${formatCurrency(s.superbollo)}` : ''}</div>
                        {getAlertBadge(s.alert)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Pagamento *</Label>
                <DateInput value={form.data_pagamento} onChange={(e) => setForm({ ...form, data_pagamento: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Data Scadenza *</Label>
                <DateInput value={form.data_scadenza} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importo € *</Label>
                <Input type="number" step="0.01" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Superbollo € (se dovuto)</Label>
                <Input type="number" step="0.01" value={form.superbollo} onChange={(e) => setForm({ ...form, superbollo: e.target.value })} />
              </div>
            </div>
            {/* Sezione Contabilità per Competenza */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium mb-3 text-muted-foreground">Contabilità per Competenza (opzionale)</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('common.costAccount')}</Label>
                  <SearchableSelect
                    options={[{ value: "__none__", label: "Nessuno" }, ...conti.map(c => ({ value: c.id, label: `${c.codice ? `[${c.codice}] ` : ''}${c.nome}` }))]}
                    value={form.conto_id || "__none__"}
                    onValueChange={(v) => setForm({ ...form, conto_id: v === "__none__" ? "" : v })}
                    placeholder={t("common.selectPlaceholder")}
                    searchPlaceholder={t("common.searchPlaceholder")}
                    emptyMessage="Nessun conto trovato"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Inizio Competenza</Label>
                  <DateInput value={form.data_inizio_competenza} onChange={(e) => setForm({ ...form, data_inizio_competenza: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Fine Competenza</Label>
                  <DateInput value={form.data_fine_competenza} onChange={(e) => setForm({ ...form, data_fine_competenza: e.target.value })} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Se compilate, queste date verranno usate per il calcolo della competenza nel bilancio</p>
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>{t("common.cancel")}</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("common.saving")}</> : (editingId ? t("common.save") : t("common.save"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog({ open, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>Eliminare questo bollo?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })} disabled={deleting}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.deleting')}</> : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog per collegamento contabilità */}
      <Dialog open={linkDialog.open} onOpenChange={(open) => setLinkDialog({ open: false, item: null, type: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {linkDialog.type === 'fattura' ? t("maintenance.linkInvoice") : t("maintenance.linkBankMovement")}
            </DialogTitle>
            <DialogDescription>
              {linkDialog.item && `Bollo ${linkDialog.item.targa} - Scadenza ${linkDialog.item.data_scadenza}`}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {linkDialog.type === 'fattura' && (
              <div className="space-y-2">
                {linkDialog.item?.fattura_id && (
                  <>
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-3">
                      <p className="text-sm font-medium text-green-800 mb-1">Fattura collegata:</p>
                      {(() => {
                        const f = fatture.find(fa => fa.id === linkDialog.item.fattura_id);
                        return f ? (
                          <div className="text-sm text-green-700">
                            <p>N. {f.numero} - {f.fornitore || 'N/D'}</p>
                            <p>Data: {f.data_emissione} &bull; Importo: &euro; {f.totale?.toFixed(2)}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-green-700">Fattura N. {linkDialog.item.fattura_numero}</p>
                        );
                      })()}
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-red-600"
                      onClick={() => handleLinkFattura(null)}
                    >{t('dialogs.removeLink')}</Button>
                  </>
                )}
                {!linkDialog.item?.fattura_id && (
                  fatture.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">{t("maintenance.noInvoices")}</p>
                  ) : (
                    fatture.map(f => (
                      <Button 
                        key={f.id} 
                        variant="outline" 
                        className="w-full justify-between"
                        onClick={() => handleLinkFattura(f.id)}
                      >
                        <span>N. {f.numero} - {f.fornitore || 'N/D'}</span>
                        <span className="text-muted-foreground">{f.data_emissione} &bull; &euro; {f.totale?.toFixed(2)}</span>
                      </Button>
                    ))
                  )
                )}
              </div>
            )}
            {linkDialog.type === 'movimento' && (
              <div className="space-y-2">
                {linkDialog.item?.movimento_bancario_id && (
                  <>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                      <p className="text-sm font-medium text-blue-800 mb-2">Movimento bancario collegato:</p>
                      {(() => {
                        const m = movimenti.find(mo => mo.id === linkDialog.item.movimento_bancario_id);
                        return m ? (
                          <div className="text-sm text-blue-700 space-y-1">
                            <p><span className="font-medium">Descrizione:</span> {m.descrizione || 'N/D'}</p>
                            <p><span className="font-medium">Data contabile:</span> {m.data}{m.data_valuta ? ` | Data valuta: ${m.data_valuta}` : ''}</p>
                            <p><span className="font-medium">Banca:</span> {m.banca_nome || 'N/D'}</p>
                            <p><span className="font-medium">Tipo:</span> {m.tipo === 'entrata' ? 'Entrata' : 'Uscita'}</p>
                            <p className={`font-medium ${m.tipo === 'entrata' ? 'text-green-600' : 'text-red-600'}`}>
                              Importo: &euro; {Math.abs(m.importo)?.toFixed(2)}
                            </p>
                            {m.note && <p><span className="font-medium">Note:</span> {m.note}</p>}
                          </div>
                        ) : (
                          <p className="text-sm text-blue-700">Movimento bancario collegato (dettagli non disponibili)</p>
                        );
                      })()}
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-red-600"
                      onClick={() => handleLinkMovimento(null)}
                    >{t('dialogs.removeLink')}</Button>
                  </>
                )}
                {!linkDialog.item?.movimento_bancario_id && (
                  movimenti.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">{t("maintenance.noMovements")}</p>
                  ) : (
                    movimenti.map(m => (
                      <Button 
                        key={m.id} 
                        variant="outline" 
                        className="w-full justify-between"
                        onClick={() => handleLinkMovimento(m.id)}
                      >
                        <span className="truncate max-w-xs">{m.descrizione || 'N/D'}</span>
                        <span className={m.tipo === 'entrata' ? 'text-green-600' : 'text-red-600'}>
                          {m.data} &bull; &euro; {Math.abs(m.importo)?.toFixed(2)}
                        </span>
                      </Button>
                    ))
                  )
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog({ open: false, item: null, type: null })}>{t("common.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Collega Allocazione a Bolli */}
      <Dialog open={collegaDialog.open} onOpenChange={(open) => { setCollegaDialog({ ...collegaDialog, open }); setSelectedBolli([]); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("roadTax.linkToTax")}</DialogTitle>
            <DialogDescription>
              Seleziona i bolli a cui collegare questa allocazione. Importo: {formatCurrency(collegaDialog.allocazione?.importo)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {bolli.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">Nessun bollo registrato. Crea prima un bollo.</p>
            ) : (
              bolli.map(bollo => {
                const auto = automezzi.find(a => a.id === bollo.automezzo_id);
                return (
                  <div 
                    key={bollo.id} 
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedBolli.includes(bollo.id) ? 'bg-purple-50 border-purple-300' : 'hover:bg-gray-50'}`}
                    onClick={() => toggleBollo(bollo.id)}
                  >
                    <Checkbox 
                      checked={selectedBolli.includes(bollo.id)} 
                      onCheckedChange={() => toggleBollo(bollo.id)}
                    />
                    <div className="flex-1">
                      <div className="font-medium">
                        {auto ? `${auto.marca} ${auto.modello} (${auto.targa})` : 'Veicolo non trovato'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatCurrency(bollo.importo)} {bollo.superbollo > 0 && `+ Superbollo ${formatCurrency(bollo.superbollo)}`}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Pagamento: {bollo.data_pagamento} • Scadenza: {bollo.data_scadenza}
                      </div>
                    </div>
                    {getAlertBadge(bollo.alert)}
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCollegaDialog({ open: false, allocazione: null }); setSelectedBolli([]); }}>{t('common.cancel')}</Button>
            <Button onClick={handleCollegaAllocazione} disabled={selectedBolli.length === 0} className="bg-purple-600 hover:bg-purple-700">
              Collega a {selectedBolli.length} bollo/i
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
